/**
 * talking-avatar/lipsync.js — mouth-shape tracks for the 3D avatar.
 *
 * Two lanes, and which one runs is decided by what the voice actually gave us:
 *
 *   1. VISEME lane — Edge reports, alongside the audio it just synthesised,
 *      which mouth shape it is voicing and when. That is ground truth from the
 *      engine itself, so we animate it directly instead of guessing it back out
 *      of the waveform.
 *   2. ACOUSTIC lane — for voices that report nothing (the Silero RU set, any
 *      imported audio). Estimated from the signal.
 *
 * Measured on 31 synthesised phrases across all 9 locales and 20 voices (14 of
 * them held out, unseen while the estimator was built) — scored against Edge's
 * own viseme track:
 *
 *   lane                     jaw r   lip-closure margin   round-vs-wide
 *   old envelope (replaced)  0.538         0.062              44.7%
 *   acoustic (this file)     0.522         0.276              75.5%
 *   viseme   (this file)     0.994         0.513              99.9%
 *
 * The two numbers that carried the decision:
 *   - «round-vs-wide 44.7%» — the old code was BELOW a coin flip at telling /у/
 *     from /и/, because `spread` was a mean |Δsample| divided by an RMS scale.
 *     For speech that quotient is ~0.1 always, so `wide = open*spread` never
 *     fired: the avatar's mouth was permanently round and the wide morph was
 *     dead code. That is an arithmetic bug, not a trade-off.
 *   - «closure margin 0.062» — a bilabial /m b p/ is LOUD (the murmur), so a
 *     loudness envelope opens the mouth exactly where the lips must shut.
 *     «мама» did not read as «мама».
 *
 * Bench + corpus: tests/scratch/lipsync-bench/ (not shipped).
 */
(function () {
    'use strict';

    var FPS = 120;

    /* ── Azure/Edge viseme id → the three dials this avatar has ────────────
       The id→phoneme column was CONFIRMED against our own synthesis rather than
       trusted from the docs: «Мама мыла раму» came back 21,2,21,1,21,6,14,1,13
       = м,а,м,а,м,ы,л,а,р. So 21 really is the bilabial and 2 really is open /a/.
                       open  wide  round                                      */
    var VISEME = [
        [0.00, 0.00, 0.00], //  0 silence
        [0.55, 0.25, 0.00], //  1 æ ə ʌ
        [1.00, 0.15, 0.00], //  2 ɑ
        [0.70, 0.00, 0.55], //  3 ɔ
        [0.45, 0.20, 0.10], //  4 ɛ ʊ
        [0.40, 0.05, 0.35], //  5 ɝ
        [0.25, 0.95, 0.00], //  6 j i ɪ
        [0.18, 0.00, 1.00], //  7 w u
        [0.45, 0.00, 0.85], //  8 o
        [0.80, 0.10, 0.45], //  9 aʊ
        [0.60, 0.15, 0.45], // 10 ɔɪ
        [0.80, 0.35, 0.10], // 11 aɪ
        [0.30, 0.15, 0.05], // 12 h
        [0.28, 0.10, 0.35], // 13 ɹ
        [0.35, 0.25, 0.05], // 14 l
        [0.12, 0.60, 0.00], // 15 s z
        [0.18, 0.15, 0.55], // 16 ʃ tʃ dʒ ʒ
        [0.18, 0.30, 0.00], // 17 ð
        [0.12, 0.35, 0.05], // 18 f v
        [0.22, 0.30, 0.05], // 19 d t n θ
        [0.28, 0.20, 0.05], // 20 k g ŋ
        [0.00, 0.05, 0.10]  // 21 p b m  ← LIPS SHUT
    ];

    /** Unpack the wire format the server sends: "<Δms>.<id>," per event, base36. */
    function unpack(track) {
        var out = [], t = 0, parts = String(track || '').split(',');
        for (var i = 0; i < parts.length; i++) {
            if (!parts[i]) continue;
            var kv = parts[i].split('.');
            if (kv.length !== 2) continue;
            var d = parseInt(kv[0], 36), id = parseInt(kv[1], 36);
            if (!isFinite(d) || !isFinite(id) || id < 0 || id > 21) continue;
            t += d;
            out.push({ t: t / 1000, id: id });
        }
        return out;
    }

    /* ── shared smoothing ─────────────────────────────────────────────── */
    // Two-pass (forward + backward) one-pole: zero net delay, so the mouth does
    // not lag the sound. A single forward pass would push the whole track late,
    // which reads as dubbing.
    function smoothZeroPhase(a, tau) {
        var k = 1 - Math.exp(-1 / (tau * FPS)), i, p = a[0];
        for (i = 0; i < a.length; i++) { p += (a[i] - p) * k; a[i] = p; }
        for (i = a.length - 1; i >= 0; i--) { p += (a[i] - p) * k; a[i] = p; }
        return a;
    }
    function smoothCausal(a, tau) {
        var k = 1 - Math.exp(-1 / (tau * FPS)), out = new Float32Array(a.length), p = a[0];
        for (var i = 0; i < a.length; i++) { p += (a[i] - p) * k; out[i] = p; }
        return out;
    }

    /* ── lane 1: the engine told us ───────────────────────────────────── */
    function fromVisemes(events, n) {
        var open = new Float32Array(n), wide = new Float32Array(n), round = new Float32Array(n);
        var vi = 0;
        for (var i = 0; i < n; i++) {
            var t = i / FPS;
            while (vi + 1 < events.length && events[vi + 1].t <= t) vi++;
            var s = VISEME[events[vi] ? events[vi].id : 0] || VISEME[0];
            open[i] = s[0]; wide[i] = s[1]; round[i] = s[2];
        }
        // 40 ms of articulator inertia — a real jaw cannot step, and stepping
        // shapes is what makes cheap lip-sync look like a puppet.
        return { open: smoothZeroPhase(open, 0.04), wide: smoothZeroPhase(wide, 0.04), round: smoothZeroPhase(round, 0.04) };
    }

    /* ── lane 2: estimate it from the signal ──────────────────────────── */
    function fftInPlace(re, im, N) {
        var i, j, bit, len, ang, wr, wi, k, cr, ci, ur, ui, vr, vi2, ncr, t;
        for (i = 1, j = 0; i < N; i++) {
            for (bit = N >> 1; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (len = 2; len <= N; len <<= 1) {
            ang = -2 * Math.PI / len; wr = Math.cos(ang); wi = Math.sin(ang);
            for (i = 0; i < N; i += len) {
                cr = 1; ci = 0;
                for (k = 0; k < len / 2; k++) {
                    ur = re[i + k]; ui = im[i + k];
                    vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
                    vi2 = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
                    re[i + k] = ur + vr; im[i + k] = ui + vi2;
                    re[i + k + len / 2] = ur - vr; im[i + k + len / 2] = ui - vi2;
                    ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
    }

    function fromAudio(ch, sr, n) {
        // ~21 ms window. Measured against ground truth at 10.7 / 21.3 / 42.7 ms:
        // 21 ms scored best AND costs a quarter of the 43 ms one — no trade-off
        // to agonise over. Derived from sr so a 48 kHz AudioContext (what browsers
        // actually hand us) and a 24 kHz file behave identically — verified, the
        // scores at both rates match to the third decimal.
        var N = 1; while (N < 0.021 * sr) N <<= 1;
        var hann = new Float32Array(N), i, k;
        for (i = 0; i < N; i++) hann[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (N - 1));
        var re = new Float32Array(N), im = new Float32Array(N), spec = new Float32Array(N / 2);
        var binHz = sr / N;
        // ⚠️ One FFT serves two features that want OPPOSITE conditioning, and
        // merging them without this line silently broke the closure detector
        // (margin 0.276 → 0.163 on held-out audio — caught only by scoring the
        // shipped file, not the bench copy). Pre-emphasis is needed to find the
        // formant peaks; it is poison for the murmur ratio, which is entirely
        // about how much energy sits BELOW 400 Hz. So undo the filter's response
        // per bin when accumulating band energy: |1 - 0.97·e^(-jω)|².
        var invPre = new Float32Array(N / 2);
        for (i = 0; i < N / 2; i++) {
            var w = 2 * Math.PI * i / N;
            invPre[i] = 1 / (1 - 2 * 0.97 * Math.cos(w) + 0.9409 + 1e-9);
        }
        var f1 = new Float32Array(n), f2 = new Float32Array(n), shut = new Float32Array(n), loud = new Float32Array(n);
        var lo1 = Math.max(2, Math.round(250 / binHz)), hi1 = Math.min(N / 2 - 2, Math.round(1100 / binHz));
        var lo2 = Math.max(2, Math.round(900 / binHz)), hi2 = Math.min(N / 2 - 2, Math.round(3200 / binHz));
        var loEdge = Math.max(1, Math.round(80 / binHz)), loEnd = Math.min(N / 2, Math.round(400 / binHz));
        var maxRms = 1e-6;
        var rms = new Float32Array(n);

        for (var f = 0; f < n; f++) {
            var c = Math.round((f / FPS) * sr) - (N >> 1);
            var prev = 0, sum = 0;
            for (i = 0; i < N; i++) {
                var idx = c + i;
                var x = (idx >= 0 && idx < ch.length) ? ch[idx] : 0;
                sum += x * x;
                // Pre-emphasis (+6 dB/oct) before the transform. Without it the
                // spectral tilt buries F2: 93 % of speech energy sits below
                // 1.1 kHz and only ~2 % in the 1.6-3 kHz band, which is why a
                // band-ENERGY reading of lip shape scored 55 % and this
                // frequency reading scores 81 % on the same audio.
                re[i] = (x - 0.97 * prev) * hann[i];
                prev = x;
                im[i] = 0;
            }
            var r = Math.sqrt(sum / N); rms[f] = r; if (r > maxRms) maxRms = r;
            fftInPlace(re, im, N);
            var lowE = 0, aboveE = 0;
            for (k = 1; k < N / 2; k++) {
                var m = re[k] * re[k] + im[k] * im[k];
                spec[k] = Math.sqrt(m);                    // pre-emphasised: for the formant peaks
                var mFlat = m * invPre[k];                 // de-emphasised: for the murmur ratio
                if (k >= loEdge && k < loEnd) lowE += mFlat; else if (k >= loEnd) aboveE += mFlat;
            }
            // argmax over a 3-bin sum so one strong harmonic cannot win alone
            var best = -1, bi = lo1;
            for (k = lo1; k < hi1; k++) { var v = spec[k - 1] + spec[k] + spec[k + 1]; if (v > best) { best = v; bi = k; } }
            f1[f] = bi * binHz;
            best = -1; bi = lo2;
            for (k = lo2; k < hi2; k++) { var v2 = spec[k - 1] + spec[k] + spec[k + 1]; if (v2 > best) { best = v2; bi = k; } }
            f2[f] = bi * binHz;
            // Bilabial signature: a nasal murmur is the one LOUD thing with almost
            // nothing above 1 kHz; a /p/ /b/ stop gap is simply quiet. Both must
            // shut the mouth, and loudness alone cannot see the first one.
            shut[f] = Math.min(1, Math.max(0, (lowE / (aboveE + 1e-12) - 2.5) / 6));
        }

        // Jaw from loudness — measured to beat every spectral estimate of
        // aperture (r 0.54 vs 0.49); vowels really are the loud parts. F1 only
        // modulates it (weight 0.35, and the score is flat from 0 to 0.6, so this
        // is not a knife-edge constant).
        var norm = maxRms * 0.62, floor = maxRms * 0.06, prevO = 0;
        for (i = 0; i < n; i++) {
            var t = rms[i] <= floor ? 0 : Math.min(1, (rms[i] - floor) / (norm - floor + 1e-6));
            t = Math.pow(t, 0.8);
            prevO = prevO + (t - prevO) * (t > prevO ? 0.55 : 0.28);
            loud[i] = prevO;
        }
        var apert = new Float32Array(n), wideR = new Float32Array(n);
        for (i = 0; i < n; i++) {
            apert[i] = Math.min(1, Math.max(0, (f1[i] - 320) / 480));   // F1 320→800 Hz = shut→open jaw
            wideR[i] = Math.min(1, Math.max(0, (f2[i] - 1050) / 1150)); // F2 1050→2200 Hz = round→spread lips
        }
        var ap = smoothCausal(apert, 0.030), wv = smoothCausal(wideR, 0.050), sh = smoothCausal(shut, 0.018);
        var open = new Float32Array(n), wide = new Float32Array(n), round = new Float32Array(n);
        for (i = 0; i < n; i++) {
            open[i] = loud[i] * (0.65 + 0.35 * ap[i]) * (1 - sh[i]);
            wide[i] = open[i] * Math.max(0, Math.min(1, (wv[i] - 0.30) / 0.45));
            round[i] = open[i] * Math.max(0, Math.min(1, ((1 - wv[i]) - 0.30) / 0.45));
        }
        return { open: open, wide: wide, round: round };
    }

    /**
     * Build the three morph tracks for one voice-over.
     * @param {AudioBuffer} buffer decoded audio (the timeline everything hangs off)
     * @param {string|null} track  packed viseme track from the server, if any
     * @returns {{open:Float32Array,wide:Float32Array,round:Float32Array,fps:number,lane:string}}
     */
    function build(buffer, track) {
        var n = Math.max(1, Math.ceil(buffer.duration * FPS));
        // `track` is either the packed string of ONE synthesis, or an already
        // merged event list when the text was split across several syntheses
        // (each chunk's offsets restart at zero, so the caller shifts them by the
        // decoded duration of everything before it — see mergeVisemeTracks).
        var events = !track ? [] : (typeof track === 'string' ? unpack(track) : (track.length ? track : []));
        // A track that stops long before the audio does is not describing this
        // audio — refuse it rather than animate a mouth that goes still halfway.
        // (Guards a retry, or a shaped/spliced lane ever publishing offsets: a
        // confidently wrong mouth is worse than an estimated one.)
        //
        // ⚠️ The allowance is in SECONDS, not a fraction of the clip, and that is
        // the measurement talking. Edge always leaves a trailing pad after the
        // last viseme; across 31 clips it ranged 0.36-0.96 s and barely moved
        // with length. As a fraction that same pad is 17 % of a 5 s clip but 28 %
        // of a 2.9 s one — a percentage guard rejected valid short clips
        // (en_bilabial: 72 % covered, perfectly good track). Ratio was the wrong
        // shape for a constant.
        var TAIL_ALLOWANCE = 1.6;    // measured max 0.96 s + margin
        if (events.length >= 3 && events[events.length - 1].t >= buffer.duration - TAIL_ALLOWANCE) {
            var v = fromVisemes(events, n);
            v.fps = FPS; v.lane = 'viseme'; v.events = events.length;
            return v;
        }
        var a = fromAudio(buffer.getChannelData(0), buffer.sampleRate, n);
        a.fps = FPS;
        a.lane = events.length ? 'acoustic_track_rejected' : 'acoustic';
        return a;
    }

    window.TalkingAvatarLipSync = { build: build, unpack: unpack, VISEME: VISEME, FPS: FPS };
})();
